import React, { useEffect, useState } from "react"
import { Button, Input, PopoverBody, PopoverHeader, Spinner, Table, UncontrolledPopover } from "reactstrap"
import moment from "moment"
import { getMustReadStatus } from "helpers/mustReadApi"

const formatReviewedAt = (iso) => (iso ? moment(iso).format("MMM D, YYYY, h:mm A") : "Date unavailable")

const ReviewBadge = ({ userId, reviewed, total, reviews }) => {
  const done = total > 0 && reviewed >= total
  const targetId = `mr-reviewed-${userId}`
  const items = reviews || []
  return (
    <>
      <span
        id={targetId}
        tabIndex={0}
        aria-label={`${reviewed} of ${total} reviewed`}
        className={`mr-status mr-status--hoverable ${done ? "mr-status--reviewed" : reviewed > 0 ? "mr-status--in-progress" : "mr-status--not-started"}`}
      >
        {reviewed} / {total}
      </span>
      <UncontrolledPopover trigger="hover focus" placement="left" target={targetId} fade={false} innerClassName="mr-review-popover">
        <PopoverHeader>Reviewed items</PopoverHeader>
        <PopoverBody>
          {items.length === 0 ? (
            <div className="text-muted">No items reviewed yet.</div>
          ) : (
            <ul className="mr-review-popover__list">
              {items.map((item) => (
                <li key={item.itemId}>
                  <div className="mr-review-popover__title">{item.title}</div>
                  <div className="text-muted small">{formatReviewedAt(item.completedAt)}</div>
                </li>
              ))}
            </ul>
          )}
        </PopoverBody>
      </UncontrolledPopover>
    </>
  )
}

const MustReadStatusTable = ({ companies }) => {
  const [companyId, setCompanyId] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      getMustReadStatus({ page, limit: 25, ...(companyId ? { companyId } : {}), ...(search ? { search } : {}) })
        .then((d) => !cancelled && setData(d))
        .catch(() => !cancelled && setData({ items: [], pagination: { page: 1, totalPages: 1 } }))
        .finally(() => !cancelled && setLoading(false))
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [companyId, search, page])

  const items = data?.items || []
  const totalPages = data?.pagination?.totalPages || 1

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Input type="select" aria-label="Filter by company" style={{ maxWidth: 260 }} value={companyId} onChange={(e) => { setPage(1); setCompanyId(e.target.value) }}>
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.isPublic ? "Public" : c.isDistributor ? "Distributors" : c.name}</option>
          ))}
        </Input>
        <Input aria-label="Search users" placeholder="Search name or email" style={{ maxWidth: 260 }} value={search} onChange={(e) => { setPage(1); setSearch(e.target.value) }} />
        {loading && <Spinner size="sm" className="align-self-center" />}
      </div>
      <Table responsive hover className="align-middle mb-2">
        <thead>
          <tr><th>User</th><th>Company</th><th>Reviewed</th><th>Last completed</th></tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading && (
            <tr><td colSpan={4} className="text-muted">No users match.</td></tr>
          )}
          {items.map((u) => (
            <tr key={u.userId}>
              <td><div className="fw-semibold">{u.name || u.email}</div><div className="text-muted small">{u.email}</div></td>
              <td>{u.companyName || "-"}</td>
              <td>
                <ReviewBadge userId={u.userId} reviewed={u.reviewed} total={u.total} reviews={u.reviews} />
              </td>
              <td>{u.lastCompletedAt ? moment(u.lastCompletedAt).format("MMM D, YYYY") : "-"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end gap-2">
        <Button size="sm" outline disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <span className="small align-self-center">Page {page} of {totalPages}</span>
        <Button size="sm" outline disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </div>
  )
}

export default MustReadStatusTable
